import { useCallback, useEffect } from "react";
import { FLYOUT_TOGGLE_DELAY, MESSAGE_DELAY } from "../constants/widget";
import {
  postModalCloseMessage,
  postFlyoutToggleMessage,
  postInterfaceHideMessage,
  postNavigationPageStructureMessage,
  postInterfaceShowMessage,
} from "../utils";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts";

interface UseWidgetKeyboardShortcutsProps {
  isFlyoutOpen: boolean;
  setIsFlyoutOpen: (open: boolean) => void;
  setIsDropdownOpen: (open: boolean) => void;
  resetSettings: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  oversizeWidget: boolean;
  setOversizeWidget: (value: boolean) => void;
}

export const useWidgetKeyboardShortcuts = ({
  isFlyoutOpen,
  setIsFlyoutOpen,
  setIsDropdownOpen,
  resetSettings,
  inputRef,
  oversizeWidget,
  setOversizeWidget,
}: UseWidgetKeyboardShortcutsProps) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Handle input field specific keys
      if (document.activeElement === inputRef.current) {
        if (event.key === "Enter") {
          event.preventDefault();
          setOversizeWidget(!oversizeWidget);
        }
        return;
      }
    },
    [inputRef, oversizeWidget, setOversizeWidget]
  );

  useKeyboardShortcuts([
    {
      key: "Escape",
      callback: () => {
        if (isFlyoutOpen) {
          setIsFlyoutOpen(false);
          setIsDropdownOpen(false);
          postFlyoutToggleMessage(false);
          postModalCloseMessage();
        }
      },
    },
    {
      key: "f",
      altKey: true,
      ctrlKey: true,
      callback: () => {
        setTimeout(() => {
          postFlyoutToggleMessage(true);
        }, MESSAGE_DELAY);
        setTimeout(() => {
          setIsFlyoutOpen(true);
        }, FLYOUT_TOGGLE_DELAY);
      },
    },
    {
      key: "h",
      altKey: true,
      ctrlKey: true,
      callback: () => postInterfaceHideMessage(),
    },
    {
      key: "s",
      altKey: true,
      ctrlKey: true,
      callback: () => postInterfaceShowMessage(),
    },
    {
      key: "r",
      altKey: true,
      ctrlKey: true,
      callback: () => resetSettings(),
    },
  ]);

  useEffect(() => {
    const targetDocument = window.document;
    targetDocument.addEventListener("keydown", handleKeyDown, true);

    window.focus();
    targetDocument.body.tabIndex = 0;
    targetDocument.body.focus();

    return () => {
      targetDocument.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [handleKeyDown]);
};
